import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import apiService from '../../services/api';
import { formatARS } from '../../utils/formatCurrency';
import ClientInvoiceEditor, { InvoiceDetail, invoiceTotals } from './ClientInvoiceEditor';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

const today = () => new Date().toLocaleDateString('sv');
const formatDMY = (iso: string) => String(iso || '').slice(0, 10).split('-').reverse().join('/');

// Invoices a monthly order in the client's cuenta corriente, as a new invoice or added to one already
// loaded: lines come pre-filled with what is not invoiced yet and the client's prices, and stay linked
// to the order, which then shows as invoiced.
const OrderInvoiceModal: React.FC<{ order: { id: number; clientId: number; clientName: string; label?: string; month: string }; onClose: () => void; onSaved: () => void }> = ({ order, onClose, onSaved }) => {
  useBodyScrollLock(true);
  useEscapeToClose(true, onClose);
  const [invoice, setInvoice] = useState<InvoiceDetail>({ number: '', items: [] });
  const [movementDate, setMovementDate] = useState(today());
  const [note, setNote] = useState('');
  const [movements, setMovements] = useState<any[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // 'nueva' or the id of the invoice the order is added to.
  const [target, setTarget] = useState('nueva');

  useEffect(() => {
    let active = true;
    apiService.getAccountMovements('client', order.clientId)
      .then((data) => { if (active) setMovements(data.movements || []); })
      .catch(() => { if (active) setMovements([]); });
    return () => { active = false; };
  }, [order.clientId]);

  // Only invoices with article detail can take more lines: a plain amount would lose its total.
  const invoices = (movements || []).filter((m) => m.movementType === 'documento' && m.category === 'factura' && m.invoice?.items?.length);
  const targetInvoice = target === 'nueva' ? null : invoices.find((m) => String(m.id) === target) || null;
  const chooseTarget = (value: string) => {
    const chosen = invoices.find((m) => String(m.id) === value);
    setError('');
    setTarget(chosen ? value : 'nueva');
    setInvoice(chosen ? { number: chosen.invoice.number || '', items: chosen.invoice.items } : { number: '', items: [] });
    if (chosen) setMovementDate(String(chosen.movementDate || today()).slice(0, 10));
  };
  const invoiceLabel = (m: any) => {
    const orders = Array.from(new Set<string>((m.invoice?.items || []).map((line: any) => line.orderLabel || line.orderMonth).filter(Boolean)));
    return `Factura ${m.invoice?.number || 'sin número'} · ${formatDMY(m.movementDate)} · ${formatARS(Number(m.amount || 0))}${orders.length ? ` · ${orders.join(', ')}` : ''}`;
  };

  const totals = invoiceTotals(invoice);
  const save = async () => {
    if (!invoice.items.length || invoice.items.some((line) => !line.productName.trim() || line.quantity <= 0)) { setError('Completá los artículos y sus cantidades.'); return; }
    if (totals.total <= 0) { setError('Cargá los precios: el total de la factura tiene que ser mayor a cero.'); return; }
    try {
      setSaving(true); setError('');
      if (targetInvoice) {
        // The invoice amount follows its new lines; date and note stay as they were.
        await apiService.updateAccountMovement(targetInvoice.id, { amount: totals.total, invoice });
      } else {
        await apiService.createAccountMovement('client', order.clientId, {
          movementType: 'documento', category: 'factura', amount: totals.total, movementDate, note: note || undefined, invoice,
        });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo guardar la factura.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-xl bg-white shadow-xl sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-4">
          <div><h2 className="text-lg font-semibold">Facturar pedido</h2><p className="text-xs text-gray-500">{order.clientName} · {order.label || order.month}. Se registra en su cuenta corriente.</p></div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-5">
          {movements !== null && invoices.length > 0 && (
            <label className="block text-sm font-medium">¿En qué factura?
              <select value={target} onChange={(e) => chooseTarget(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2">
                <option value="nueva">Factura nueva</option>
                {invoices.map((m) => <option key={m.id} value={String(m.id)}>Agregar a: {invoiceLabel(m)}</option>)}
              </select>
            </label>
          )}
          {targetInvoice && <p className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">Se agregan los artículos de este pedido a la factura {targetInvoice.invoice.number || 'sin número'}. Su total pasa de <strong>{formatARS(Number(targetInvoice.amount || 0))}</strong> a <strong>{formatARS(totals.total)}</strong>.</p>}
          {!targetInvoice && <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium">Fecha<input type="date" value={movementDate} onChange={(e) => setMovementDate(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
            <label className="text-sm font-medium">Nota (opcional)<input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
          </div>}
          {movements === null ? <p className="py-6 text-center text-sm text-gray-500">Cargando...</p> : (
            <ClientInvoiceEditor key={target} clientId={order.clientId} invoice={invoice} onChange={setInvoice} movements={movements} editingId={targetInvoice ? targetInvoice.id : null} autoOrderId={order.id} />
          )}
          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        </div>
        <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t bg-white px-5 py-4">
          <span className="text-sm text-gray-600">Total: <strong>{formatARS(totals.total)}</strong></span>
          <div className="flex gap-2"><button onClick={onClose} className="rounded-lg border px-4 py-2">Cancelar</button><button onClick={save} disabled={saving || movements === null} className="rounded-lg bg-primary-600 px-4 py-2 text-white disabled:opacity-50">{saving ? 'Guardando...' : targetInvoice ? 'Agregar a la factura' : 'Guardar factura'}</button></div>
        </div>
      </div>
    </div>
  );
};

export default OrderInvoiceModal;
